import { useCallback, useRef, useState } from 'react';

import { QueryResult } from '../types';


// Keys that indicate the JSON object is metadata/config, not a query result
const METADATA_KEYS = new Set( [ 'type', 'error', 'message', 'status', 'code', 'name', 'description' ] );

/**
 * Attempt to extract tabular data from an agent's text response.
 * Handles: JSON code blocks, raw JSON arrays, markdown tables.
 */
export function extractResultFromText( text: string ): QueryResult | null {
  // 1. Try JSON code block: ```json [...] ```
  const jsonBlockMatch = text.match( /```(?:json)?\s*(\[[\s\S]*?\])\s*```/ );
  if ( jsonBlockMatch ) {
    try {
      const rows = JSON.parse( jsonBlockMatch[1] ) as Record<string, unknown>[];
      if ( Array.isArray( rows ) && rows.length > 0 ) {
        return { columns: Object.keys( rows[0] ), rows };
      }
    } catch { /* not valid JSON */ }
  }

  // 2. Try raw JSON array in text
  const rawJsonMatch = text.match( /(\[[\s\S]*?\{[\s\S]*?\}[\s\S]*?\])/ );
  if ( rawJsonMatch ) {
    try {
      const rows = JSON.parse( rawJsonMatch[1] ) as Record<string, unknown>[];
      if ( Array.isArray( rows ) && rows.length > 0 ) {
        return { columns: Object.keys( rows[0] ), rows };
      }
    } catch { /* not valid JSON */ }
  }

  // 3. Try single JSON object (e.g. COUNT(*) result)
  // Exclude objects that look like metadata (have keys like 'type', 'error', etc.)
  const singleObjMatch = text.match( /(\{[^{}]*\})/ );
  if ( singleObjMatch ) {
    try {
      const obj = JSON.parse( singleObjMatch[1] ) as Record<string, unknown>;
      const keys = Object.keys( obj );
      const isMetadata = keys.length > 0 && keys.every( k => METADATA_KEYS.has( k.toLowerCase() ) );
      if ( typeof obj === 'object' && obj !== null && !isMetadata && keys.length > 0 ) {
        return { columns: keys, rows: [ obj ] };
      }
    } catch { /* not valid JSON */ }
  }

  // 4. Try markdown table (pipe-delimited)
  const lines = text.split( '\n' ).filter( l => l.trim().startsWith( '|' ) );
  if ( lines.length >= 3 ) {
    const headerLine = lines[0];
    const dataLines = lines.slice( 2 ); // skip header + separator

    const columns = headerLine
      .split( '|' )
      .map( c => c.trim() )
      .filter( Boolean );

    const rows = dataLines.map( line => {
      const values = line.split( '|' ).map( c => c.trim() ).filter( Boolean );
      const row: Record<string, unknown> = {};
      columns.forEach( ( col, i ) => {
        row[col] = values[i] ?? '';
      } );
      return row;
    } );

    if ( columns.length > 0 && rows.length > 0 ) {
      return { columns, rows };
    }
  }

  // 5. Try whitespace/tab-separated table (e.g. agent prose with inline table)
  const allLines = text.split( '\n' ).map( l => l.trim() ).filter( Boolean );
  for ( let i = 0; i < allLines.length - 1; i++ ) {
    const headerLine = allLines[i];
    // Header must have 2+ columns separated by 2+ spaces or a tab
    const headerCols = headerLine.split( /\t|  +/ ).map( s => s.trim() ).filter( Boolean );
    if ( headerCols.length < 2 ) continue;

    // Collect data rows immediately following the header
    const dataRows: Record<string, unknown>[] = [];
    for ( let j = i + 1; j < allLines.length; j++ ) {
      const rowCols = allLines[j].split( /\t|  +/ ).map( s => s.trim() ).filter( Boolean );
      if ( rowCols.length < headerCols.length ) break;
      const row: Record<string, unknown> = {};
      headerCols.forEach( ( col, idx ) => { row[col] = rowCols[idx] ?? ''; } );
      dataRows.push( row );
    }

    if ( dataRows.length > 0 ) {
      return { columns: headerCols, rows: dataRows };
    }
  }

  return null;
}

export function useQueryExecution() {
  const [ queryResult, setQueryResult ] = useState<QueryResult | null>( null );
  const [ isExecuting, setIsExecuting ] = useState( false );
  const executionStartRef = useRef<number | null>( null );

  const startExecution = useCallback( () => {
    executionStartRef.current = Date.now();
    setIsExecuting( true );
    setQueryResult( null );
  }, [] );

  // Update result without stopping the execution timer (for progressive batch delivery)
  const updateResult = useCallback( ( result: QueryResult ) => {
    setQueryResult( prev => ( {
      ...result,
      executionTime: prev?.executionTime
    } ) );
  }, [] );

  const completeExecution = useCallback( ( result: QueryResult | null ) => {
    setIsExecuting( false );
    if ( result ) {
      const elapsed = executionStartRef.current ? ( Date.now() - executionStartRef.current ) / 1000 : undefined;
      setQueryResult( { ...result, executionTime: elapsed } );
    }
    executionStartRef.current = null;
  }, [] );

  const setError = useCallback( ( error: string ) => {
    setIsExecuting( false );
    setQueryResult( { columns: [], rows: [], error } );
    executionStartRef.current = null;
  }, [] );

  const clearResult = useCallback( () => {
    setQueryResult( null );
  }, [] );

  return {
    queryResult,
    isExecuting,
    startExecution,
    updateResult,
    completeExecution,
    setError,
    clearResult
  };
}
